import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { WS_URL } from '../../config';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Users,
  Truck,
  MapPin,
  Phone,
  Mail,
  Store,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  History,
  Eye,
  Search,
  ShoppingCart,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const Repartidores = () => {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [activeTab, setActiveTab] = useState('drivers'); // 'drivers' or 'history'
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    branch_ids: [],
    capabilities: ['marketplace']
  });

  const wsRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [user]);

  // WebSocket: listen for real-time driver status changes
  useEffect(() => {
    if (!user?.business_id) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}/api/v1/delivery/ws/business/${user.business_id}?token=${token}`);
      ws.onopen = () => {
        const ping = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
        ws._ping = ping;
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'driver_status') {
            setDrivers(prev => prev.map(d =>
              d.id === msg.driver_id ? { ...d, status: msg.status } : d
            ));
          }
        } catch {}
      };
      ws.onclose = () => {
        clearInterval(ws._ping);
        setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
      wsRef.current = ws;
    };

    connect();
    return () => {
      if (wsRef.current) {
        clearInterval(wsRef.current._ping);
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [user?.business_id]);

  const loadData = async () => {
    if (!user?.business_id) return;

    try {
      const [driversRes, branchesRes] = await Promise.all([
        api.get(`/delivery/drivers/business/${user.business_id}`),
        api.get(`/branches?business_id=${user.business_id}`)
      ]);
      
      setDrivers(driversRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryHistory = async () => {
    if (!user?.business_id) return;
    
    try {
      const res = await api.get(`/delivery/history/business/${user.business_id}`);
      setDeliveryHistory(res.data.deliveries || []);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Error al cargar historial');
    }
  };

  const handleOpenModal = (driver = null) => {
    if (driver) {
      setEditingDriver(driver);
      setFormData({
        full_name: driver.full_name,
        email: driver.email,
        phone: driver.phone,
        password: '',
        branch_ids: driver.branch_ids || [],
        capabilities: (driver.capabilities && driver.capabilities.length > 0)
          ? driver.capabilities
          : ['marketplace']
      });
    } else {
      setEditingDriver(null);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        password: '',
        branch_ids: [],
        capabilities: ['marketplace']
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDriver(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    if (!editingDriver && !formData.password) {
      toast.error('La contraseña es requerida para nuevos repartidores');
      return;
    }

    if (formData.branch_ids.length === 0) {
      toast.error('Asigna al menos una sucursal');
      return;
    }

    if (!formData.capabilities || formData.capabilities.length === 0) {
      toast.error('Selecciona al menos un tipo de entrega');
      return;
    }

    try {
      if (editingDriver) {
        const updateData = {
          full_name: formData.full_name,
          phone: formData.phone,
          branch_ids: formData.branch_ids,
          capabilities: formData.capabilities
        };
        await api.put(`/delivery/drivers/${editingDriver.id}`, updateData);
        toast.success('Repartidor actualizado');
      } else {
        await api.post('/delivery/drivers', {
          ...formData,
          business_id: user.business_id
        });
        toast.success('Repartidor creado');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar'));
    }
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('¿Estás seguro de desactivar este repartidor?')) return;

    try {
      await api.delete(`/delivery/drivers/${driverId}`);
      toast.success('Repartidor desactivado');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al desactivar'));
    }
  };

  const toggleBranch = (branchId) => {
    setFormData(prev => ({
      ...prev,
      branch_ids: prev.branch_ids.includes(branchId)
        ? prev.branch_ids.filter(id => id !== branchId)
        : [...prev.branch_ids, branchId]
    }));
  };

  const toggleCapability = (cap) => {
    setFormData(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(cap)
        ? prev.capabilities.filter(c => c !== cap)
        : [...prev.capabilities, cap]
    }));
  };

  // Toggle rápido desde la tabla (sin abrir modal)
  const quickToggleCapability = async (driver, cap) => {
    const current = (driver.capabilities && driver.capabilities.length > 0)
      ? driver.capabilities
      : ['marketplace'];
    const next = current.includes(cap)
      ? current.filter(c => c !== cap)
      : [...current, cap];
    if (next.length === 0) {
      toast.error('El chofer debe tener al menos un tipo de entrega');
      return;
    }
    // Optimista
    setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, capabilities: next } : d));
    try {
      await api.put(`/delivery/drivers/${driver.id}`, { capabilities: next });
      toast.success('Capacidades actualizadas');
    } catch (error) {
      setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, capabilities: current } : d));
      toast.error(getErrorMessage(error, 'Error al actualizar'));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { color: 'bg-green-100 text-green-700', label: 'Disponible', icon: CheckCircle },
      on_route: { color: 'bg-blue-100 text-blue-700', label: 'En ruta', icon: Truck },
      offline: { color: 'bg-gray-100 text-gray-600', label: 'Desconectado', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.offline;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Repartidores</h1>
            <p className="text-gray-500 mt-1">Administra tu equipo de entregas a domicilio</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            data-testid="add-driver-btn"
          >
            <Plus className="w-4 h-4" />
            Nuevo Repartidor
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('drivers')}
              className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'drivers'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Repartidores ({drivers.length})
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('history');
                loadDeliveryHistory();
              }}
              className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Historial de Entregas
              </div>
            </button>
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'drivers' ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{drivers.length}</p>
                    <p className="text-sm text-gray-500">Total Repartidores</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {drivers.filter(d => d.status === 'available').length}
                    </p>
                    <p className="text-sm text-gray-500">Disponibles</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {drivers.filter(d => d.status === 'on_route').length}
                    </p>
                    <p className="text-sm text-gray-500">En Ruta</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <XCircle className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {drivers.filter(d => d.status === 'offline').length}
                    </p>
                    <p className="text-sm text-gray-500">Desconectados</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Drivers Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {drivers.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay repartidores</h3>
                  <p className="text-gray-500 mb-4">Agrega tu primer repartidor para gestionar entregas</p>
                  <button
                    onClick={() => handleOpenModal()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Agregar Repartidor
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="drivers-table">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Repartidor</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursales</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipos de entrega</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entregas</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {drivers.map(driver => (
                        <tr key={driver.id} className="hover:bg-gray-50" data-testid={`driver-row-${driver.id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Truck className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{driver.full_name}</p>
                                <p className="text-sm text-gray-500">{driver.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone className="w-4 h-4" />
                              {driver.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {driver.branch_names?.map((name, idx) => (
                                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                  {name}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {(() => {
                              const caps = (driver.capabilities && driver.capabilities.length > 0)
                                ? driver.capabilities
                                : ['marketplace'];
                              const hasMk = caps.includes('marketplace');
                              const hasRm = caps.includes('remissions');
                              return (
                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => quickToggleCapability(driver, 'marketplace')}
                                    title={hasMk ? 'Quitar marketplace / menú' : 'Habilitar marketplace / menú'}
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                                      hasMk
                                        ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                                        : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50 line-through'
                                    }`}
                                    data-testid={`cap-toggle-marketplace-${driver.id}`}
                                  >
                                    <ShoppingCart className="w-3 h-3" />
                                    Marketplace
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => quickToggleCapability(driver, 'remissions')}
                                    title={hasRm ? 'Quitar remisiones' : 'Habilitar remisiones'}
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                                      hasRm
                                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                                        : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50 line-through'
                                    }`}
                                    data-testid={`cap-toggle-remissions-${driver.id}`}
                                  >
                                    <FileText className="w-3 h-3" />
                                    Remisiones
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4">
                            {getStatusBadge(driver.status)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <p className="font-medium">{driver.total_deliveries || 0}</p>
                              <p className="text-gray-500">entregas</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleOpenModal(driver)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(driver.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Desactivar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Delivery History Tab */
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {deliveryHistory.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Sin historial de entregas</h3>
                <p className="text-gray-500">Las entregas completadas aparecerán aquí</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pedido</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Repartidor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dirección</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Evidencia</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {deliveryHistory.map(delivery => (
                      <tr key={delivery.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">
                          {delivery.order_id?.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4">{delivery.driver_name}</td>
                        <td className="px-6 py-4">{delivery.customer_name}</td>
                        <td className="px-6 py-4 max-w-xs truncate" title={delivery.delivery_address}>
                          {delivery.delivery_address}
                        </td>
                        <td className="px-6 py-4 font-medium">${delivery.total?.toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(delivery.completed_at).toLocaleDateString('es-MX', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4">
                          {delivery.proof_photo_url ? (
                            <button className="text-blue-600 hover:underline flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              Ver foto
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleCloseModal} />
            
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editingDriver ? 'Editar Repartidor' : 'Nuevo Repartidor'}
                    </h3>
                    <button type="button" onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="bg-white px-6 py-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Nombre del repartidor"
                      data-testid="driver-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Correo Electrónico *
                    </label>
                    <input
                      type="email"
                      required
                      disabled={!!editingDriver}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      placeholder="correo@ejemplo.com"
                      data-testid="driver-email-input"
                    />
                    {editingDriver && (
                      <p className="text-xs text-gray-500 mt-1">El correo no se puede cambiar</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="10 dígitos"
                      data-testid="driver-phone-input"
                    />
                  </div>

                  {!editingDriver && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contraseña *
                      </label>
                      <input
                        type="password"
                        required
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Mínimo 6 caracteres"
                        minLength={6}
                        data-testid="driver-password-input"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sucursales Asignadas *
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {branches.length === 0 ? (
                        <p className="text-gray-500 text-sm">No hay sucursales disponibles</p>
                      ) : (
                        branches.map(branch => (
                          <label
                            key={branch.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.branch_ids.includes(branch.id)}
                              onChange={() => toggleBranch(branch.id)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <div className="flex items-center gap-2">
                              <Store className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{branch.name}</span>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      El repartidor podrá elegir de qué sucursal tomar pedidos al iniciar sesión
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipos de entrega *
                    </label>
                    <div className="space-y-2 border border-gray-200 rounded-lg p-3">
                      <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.capabilities.includes('marketplace')}
                          onChange={() => toggleCapability('marketplace')}
                          className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          data-testid="cap-marketplace"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <ShoppingCart className="w-4 h-4 text-blue-600" />
                            Pedidos de marketplace / menú digital
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Usa la PWA <code className="px-1 bg-gray-100 rounded">/repartidor</code>. Flujo de pedidos online tradicional.
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.capabilities.includes('remissions')}
                          onChange={() => toggleCapability('remissions')}
                          className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          data-testid="cap-remissions"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <FileText className="w-4 h-4 text-indigo-600" />
                            Remisiones de enLocal Caja
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Usa la PWA <code className="px-1 bg-gray-100 rounded">/delivery_negocios</code>. Remisiones surtidas en el punto de venta.
                          </div>
                        </div>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Un chofer puede operar ambos flujos. Se requiere al menos uno.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    data-testid="save-driver-btn"
                  >
                    {editingDriver ? 'Guardar Cambios' : 'Crear Repartidor'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Repartidores;
